import { useMemo, useState } from "react";
import { useParams, useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Flex,
  SimpleGrid,
  Select,
  IconButton,
  HStack,
  Text,
  Heading,
  Skeleton,
  Wrap,
  WrapItem,
  Tag,
} from "@chakra-ui/react";
import { HamburgerIcon, ViewIcon } from "@chakra-ui/icons";
import { Helmet } from "react-helmet-async";
import { useCategories } from "../hooks/useCategories";
import { useProducts } from "../hooks/useProducts";
import Breadcrumb from "../components/Breadcrumb";
import FilterSidebar, {
  EMPTY_FILTER_STATE,
  applyFilters,
  type FilterState,
} from "../components/FilterSidebar";
import ProductCard from "../components/ProductCard";

const PAGE_SIZE = 12;

type SortOption = "relevance" | "price-asc" | "price-desc" | "name-asc";

const CategoryPage = () => {
  const { categorySlug, subcategorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { categories } = useCategories();
  const { products, isLoading } = useProducts({
    categorySlug: categorySlug ?? "",
    subcategorySlug,
  });

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTER_STATE);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [page, setPage] = useState(1);

  const sort = (searchParams.get("sort") as SortOption) || "relevance";

  const category = categories.find((c) => c.slug === categorySlug);
  const subcategory = category?.subcategories.find(
    (s) => s.slug === subcategorySlug,
  );

  const filtered = useMemo(() => applyFilters(products, filters), [products, filters]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    switch (sort) {
      case "price-asc":
        return list.sort((a, b) => a.price - b.price);
      case "price-desc":
        return list.sort((a, b) => b.price - a.price);
      case "name-asc":
        return list.sort((a, b) => a.name.localeCompare(b.name));
      default:
        return list;
    }
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSortChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("sort", value);
      return next;
    });
    setPage(1);
  };

  const title = subcategory?.name ?? category?.name ?? "Category";

  return (
    <Box as="main" bg="rs.bg" minH="100vh">
      <Helmet>
        <title>{title} | Red Shutter Camera Store</title>
      </Helmet>
      <Container maxW="container.xl" px={{ base: 4, md: 6 }} py={10}>
        <Breadcrumb
          crumbs={[
            { label: category?.name ?? "Category", to: category ? `/category/${category.slug}` : undefined },
            ...(subcategory ? [{ label: subcategory.name }] : []),
          ]}
          count={sorted.length}
        />

        <Heading
          fontFamily="'DM Serif Display', serif"
          fontSize={{ base: "26px", md: "32px" }}
          fontWeight="400"
          color="rs.text"
          mb={2}
        >
          {title}
        </Heading>
        <Text fontSize="13px" color="rs.textMuted" mb={8}>
          {sorted.length} item{sorted.length === 1 ? "" : "s"}
        </Text>

        {category && category.subcategories.length > 0 && (
          <Wrap mb={8} spacing={2}>
            <WrapItem>
              <Tag
                as={RouterLink}
                to={`/category/${category.slug}`}
                size="md"
                variant={!subcategorySlug ? "solid" : "outline"}
                colorScheme="red"
                cursor="pointer"
              >
                All
              </Tag>
            </WrapItem>
            {category.subcategories.map((s) => (
              <WrapItem key={s.slug}>
                <Tag
                  as={RouterLink}
                  to={`/category/${category.slug}/${s.slug}`}
                  size="md"
                  variant={subcategorySlug === s.slug ? "solid" : "outline"}
                  colorScheme="red"
                  cursor="pointer"
                >
                  {s.name}
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}

        <Flex direction={{ base: "column", md: "row" }} gap={8}>
          <FilterSidebar
            products={products}
            filters={filters}
            onChange={(f) => {
              setFilters(f);
              setPage(1);
            }}
          />

          <Box flex={1}>
            <Flex justify="space-between" align="center" mb={5} flexWrap="wrap" gap={3}>
              <Select
                value={sort}
                onChange={(e) => handleSortChange(e.target.value)}
                w="200px"
                size="sm"
                borderColor="rs.border"
                bg="rs.surface"
              >
                <option value="relevance">Sort: Relevance</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="name-asc">Name: A-Z</option>
              </Select>

              <HStack spacing={1}>
                <IconButton
                  aria-label="Grid view"
                  icon={<ViewIcon />}
                  size="sm"
                  variant={viewMode === "grid" ? "solid" : "outline"}
                  onClick={() => setViewMode("grid")}
                />
                <IconButton
                  aria-label="List view"
                  icon={<HamburgerIcon />}
                  size="sm"
                  variant={viewMode === "list" ? "solid" : "outline"}
                  onClick={() => setViewMode("list")}
                />
              </HStack>
            </Flex>

            {isLoading ? (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} h="320px" borderRadius="10px" />
                ))}
              </SimpleGrid>
            ) : pageItems.length === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={20}
                bg="rs.surface"
                border="1px solid"
                borderColor="rs.border"
                borderRadius="10px"
              >
                <Text color="rs.textMuted">No products match your filters.</Text>
              </Flex>
            ) : (
              <SimpleGrid
                columns={
                  viewMode === "grid" ? { base: 1, sm: 2, lg: 3 } : { base: 1 }
                }
                spacing={4}
              >
                {pageItems.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </SimpleGrid>
            )}

            {totalPages > 1 && (
              <HStack justify="center" spacing={2} mt={10}>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <Box
                    key={i}
                    as="button"
                    onClick={() => setPage(i + 1)}
                    px={3}
                    py={1}
                    borderRadius="6px"
                    fontSize="13px"
                    bg={page === i + 1 ? "rs.red" : "transparent"}
                    color={page === i + 1 ? "white" : "rs.textMuted"}
                    border="1px solid"
                    borderColor={page === i + 1 ? "rs.red" : "rs.border"}
                  >
                    {i + 1}
                  </Box>
                ))}
              </HStack>
            )}
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default CategoryPage;
